import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { sortBy } from 'lodash';
import { PanModalComponent } from '../../../components/pan-modal/pan-modal.component';
import { copyToClipboard } from '../../../public/util/navigator.utils';
import { LoggedInCallback } from '../../../service/cognito.service';
import { NotifyService } from '../../../service/notify.service';
import { PosthogService } from '../../../service/posthog.service';
import { TheftReportService } from '../../../service/theft-report.service';
import { UserLoginService } from '../../../service/user-login.service';
import {
  FeatureFlag,
  FileAttachment,
  ShareCasesRequest,
  SharedInvestigation,
  TheftReportAttachmentType,
  TimelineRow,
} from '../../../types';
import { finalize } from 'rxjs/operators';
import { convertDateToTimezone } from '../../../date.utils';
import { deriveCameraName, deriveThumbnailUrl, supportsThumbnail } from './shared-investigation.utils';

@Component({
  selector: 'app-shared-investigation',
  templateUrl: './shared-investigation.component.html',
  styleUrls: ['./shared-investigation.component.scss'],
  standalone: false,
})
export class SharedInvestigationComponent implements OnInit, LoggedInCallback {
  userService = inject(UserLoginService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private theftReportService = inject(TheftReportService);
  private posthogService = inject(PosthogService);

  private notifyService = inject(NotifyService);

  public sharedInvestigation = signal<SharedInvestigation | null>(null);
  public isLoading = signal<boolean>(false);
  public isSharing = signal<boolean>(false);
  public timelineRows = computed<TimelineRow[]>(() => {
    const timeline = this.sharedInvestigation()?.timeline;
    if (!timeline) return [];
    return sortBy(
      timeline.map((item) => ({
        when: item.occurred,
        transactionId: item.platform_id,
        timestamp: item.trans_date.toLocaleString(),
        items: item.items.map((i) => `${i.description} ($${Number(i.total).toFixed(2)})`).join('\n'),
        highlight: item.occurred === 'walkout' || item.occurred === 'cancellation',
      })),
      (row) => row.timestamp,
    );
  });

  reviewId = signal<string>('');

  public theftTypes = computed(() => {
    const investigation = this.sharedInvestigation();
    if (!investigation) return [];

    return Object.keys(investigation.theft_type ?? {})
      .filter((key) => investigation.theft_type[key as keyof typeof investigation.theft_type])
      .map((key) => (key.includes('is_') ? key.replace('is_', '') : key));
  });

  public sharedReportPhotoUrls = computed(
    () =>
      this.sharedInvestigation()
        ?.attachments?.filter((a) => a.type === TheftReportAttachmentType.ReportImage)
        .map((a) => a.content) ?? [],
  );
  public sharedIncidentPhotoUrls = computed(
    () =>
      this.sharedInvestigation()
        ?.attachments?.filter((a) => a.type === TheftReportAttachmentType.InstancePhoto)
        .map((a) => a.content) ?? [],
  );
  public sharedVideoLinksAndPasswords = computed(
    () =>
      this.sharedInvestigation()
        ?.attachments?.filter((a) => a.type === TheftReportAttachmentType.VideoLink)
        .map((a) => ({ link: a.content, password: a.password })) ?? [],
  );

  public sharedFileAttachments = computed<FileAttachment[]>(
    () =>
      this.sharedInvestigation()
        ?.attachments?.filter((a) => a.type === TheftReportAttachmentType.Attachment)
        .map((a) => ({
          url: a.content,
          name: deriveCameraName(a.content),
          thumbnailUrl: deriveThumbnailUrl(a.content),
          hasThumbnail: supportsThumbnail(a.content),
          downloadUrl: a.download_url ?? a.content,
        })) ?? [],
  );

  public brokenThumbnails = signal<ReadonlySet<string>>(new Set());

  public markThumbnailBroken(url: string): void {
    this.brokenThumbnails.update((set) => new Set(set).add(url));
  }

  public sharedVideoClips = computed(() => this.sharedInvestigation()?.video_links ?? []);
  public isDiscussionsEnabled = computed(
    () => this.posthogService.isReady() && this.posthogService.isFeatureEnabled(FeatureFlag.EnableDiscussions, false),
  );

  public dateOfInstanceInTimezone = computed(() => {
    const investigation = this.sharedInvestigation();
    if (!investigation) return '';
    const date = investigation?.date_of_instance;
    if (!date) return '';
    return convertDateToTimezone(date, investigation.location.timezone);
  });

  public isSideload = computed(() => {
    const investigation = this.sharedInvestigation();
    if (!investigation) return false;
    return investigation.review_subject === 'Sideloaded';
  });

  attempts = 0;

  ngOnInit() {
    this.refresh();
    this.route.params.subscribe((params) => {
      this.posthogService.capture('access_theft_report_details', {
        cancellationId: null,
        investigationId: params.reviewId,
      });
    });
  }

  /**
   * Refresh the backend data. Since some branches require authenticated
   * users for this report, backend may return 401 in which case we authenticate
   * and then retry.
   */
  refresh() {
    this.isLoading.set(true);

    // fetch posthog info
    this.route.params.subscribe((params) => {
      this.reviewId.set(params.reviewId);
      this.posthogService.capture('view_theft_report_details', {
        cancellationId: null,
        investigationId: params.reviewId,
      });
    });

    const fromParam = this.route.snapshot.queryParams['from'];

    // fetch investigation details
    this.theftReportService.getSharedInvestigation(this.reviewId(), fromParam).subscribe(
      (investigation) => {
        this.sharedInvestigation.set(investigation);
        this.isLoading.set(false);
      },
      (error) => {
        this.isLoading.set(false);
        if (error.status === 401) {
          this.attempts++;
          if (this.attempts === 1) {
            this.userService.navigateToLogin(this.route.snapshot.data.redirect_uri);
          }
        } else if (error.status === 403) {
          try {
            this.router.navigate(['/403']);
          } catch (e) {
            console.error(e);
          }
        }
      },
    );
  }

  // attempt to login or refresh the data
  isLoggedIn(message: string, isLoggedIn: boolean) {
    if (isLoggedIn) {
      this.refresh();
    } else {
      this.route.data.subscribe((data) => this.userService.navigateToLogin(data.redirect_uri));
    }
  }

  async copyToClipboard($event: Event | null, text: string) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }

    try {
      await copyToClipboard(text);
      this.notifyService.notify('Video password copied');
    } catch {
      this.notifyService.notify('Failed to copy the password to clipboard, please copy it manually');
    }

    if ($event) {
      return false;
    }
  }

  openVideo(video: { link: string; password?: string }, modal: PanModalComponent) {
    if (video.password) {
      modal.show();
      return;
    }

    window.open(video.link, '_blank');
  }

  share(shareData: Partial<ShareCasesRequest>) {
    const reviewId = this.reviewId();
    if (!reviewId) return;
    this.isSharing.set(true);
    this.theftReportService
      .shareInvestigation(reviewId, shareData)
      .pipe(finalize(() => this.isSharing.set(false)))
      .subscribe({
        next: () => this.notifyService.notify('Successfully shared investigation'),
        error: () => this.notifyService.notify('Failed to share investigation'),
      });
  }
}
